/********1st-optimised-new-code-used-in-prod********************************************************************************************************************************************************************************************************************************* */
import { createContext, useContext, useEffect, useRef } from "react";
import { Outlet } from "react-router-dom";
import { TourProvider, useTour } from "@reactour/tour";

export const GameRefsContext = createContext(undefined);
export const useGameRefs = () => useContext(GameRefsContext);

const ScrollToTopOnFirstStep = () => {
  const { isOpen, currentStep } = useTour();

  useEffect(() => {
    if (!isOpen || currentStep !== 0) return;

    const id = setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 50);

    return () => clearTimeout(id);
  }, [isOpen, currentStep]);

  return null;
};

const DisableScrollWhenTourOpen = () => {
  const { isOpen } = useTour();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return null;
};
const ResetTourOnClose = () => {
  const { isOpen, currentStep, setCurrentStep } = useTour();

  useEffect(() => {
    if (!isOpen && currentStep !== 0) {
      setCurrentStep(0);
    }
  }, [isOpen, currentStep, setCurrentStep]);

  return null;
};

const CustomNavigation = () => {
  const { setIsOpen, currentStep, steps, setCurrentStep } = useTour();
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    // if (isLastStep) setIsOpen(false);
    // else setCurrentStep((prev) => prev + 1);
    if (isLastStep) {
      setCurrentStep(0);
      setIsOpen(false);
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSkip = () => {
    setCurrentStep(0);
    setIsOpen(false);
  };

  return (
    <div className="flex justify-between mt-3">
      <button
        onClick={handlePrev}
        className="border border-[#00ff88] text-[#00ff88] px-3 py-1 rounded hover:bg-[#00ff88] hover:text-black transition"
      >
        Prev
      </button>

      <div className="flex gap-2">
        <button onClick={handleSkip} className="text-gray-400 hover:text-white">
          Skip
        </button>
        <button
          onClick={handleNext}
          className="bg-[#00ff88] text-black px-3 py-1 rounded hover:bg-[#00cc6f] transition"
        >
          {isLastStep ? "Finish" : "Next"}
        </button>
      </div>
    </div>
  );
};

const ActiveMaskTracker = () => {
  const { currentStep, steps, isOpen, setCurrentStep } = useTour();

  useEffect(() => {
    if (!isOpen || !steps[currentStep]) return;

    const selector = steps[currentStep]?.selector;
    const element =
      typeof selector === "string"
        ? document.querySelector(selector)
        : typeof selector === "function"
          ? selector()
          : selector?.current;

    if (!element) return;

    const observer = new ResizeObserver(() => {
      setCurrentStep((prev) => prev);
    });

    const mutationObserver = new MutationObserver(() => {
      setCurrentStep((prev) => prev);
    });

    observer.observe(element);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    const handleScroll = () => setCurrentStep((prev) => prev);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, currentStep, steps, setCurrentStep]);

  return null;
};

const GameLayout = () => {
  const jackpotRef = useRef(null);
  const coinFlipRef = useRef(null);
  const betFormRef = useRef(null);
  const historyRef = useRef(null);
  const balanceRef = useRef(null);
  const depositRef = useRef(null);
  const betSideRef = useRef(null);
  const pointsRef = useRef(null);
  const betAmountRef = useRef(null);
  const betChoiceRef = useRef(null);
  const tabsRef = useRef(null);
  const winnersRef = useRef(null);

  const steps = [
    {
      selector: "#welcome-section",
      content: (
        <p>
          😊 Welcome to Heads or Tails — pick a side, flip once, and see if you
          win. Press NEXT to continue.
        </p>
      ),
    },
    {
      selector: "#balance-section",
      content:
        "💰 Track your account balance here and when you win your balance will update.",
    },
    {
      selector: "#deposit-section",
      content: "Click the button to deposit funds to your account.",
    },
    {
      selector: "#bet-side-section",
      content: "Track which side other players have choosen here.",
    },
    {
      selector: "#jackpot-section",
      content: "💰 This is the Jackpot section showing total pool value.",
    },
    {
      selector: "#points-section",
      content:
        "Earn points everytime you bet and get to see how many points you have here.Click the points button to redeem your points to cash",
    },
    {
      selector: "#coin-flip-section",
      content: "🪙 Watch the coin flip here to see which side wins.",
    },
    {
      selector: "#bet-form-section",
      content: "🎯 Choose your side and place your bet here!",
    },
    {
      selector: "#bet-amount-section",
      content:
        "💵 Pick an amount from the preset values or enter your desired amount in the input section",
    },
    {
      selector: "#bet-choice-section",
      content:
        "You can either choose HEADS or TAILS and then press Place Your bet to confirm your pick",
    },
    {
      selector: "#winners-section",
      content:
        "↗️ Toggle between daily,weekly or monthly top winners to see the top winnings.",
    },
    {
      selector: "#history-section",
      content: "📜 Check past results to see how rounds went.",
    },
    {
      selector: "#tabs-section",
      content: "📂 View your Active Bets, Bet History, and Top Wins here.",
    },
  ];

  return (
    <TourProvider
      steps={steps}
      scrollSmooth
      padding={12}
      className="reactour-dark"
      showBadge={false}
      showDots
      showCloseButton
      components={{ Navigation: CustomNavigation }}
      styles={{
        popover: (base) => ({
          ...base,
          background: "black",
          color: "white",
          borderRadius: "18px",
          border: "2px solid #00ff88",
        }),
        maskArea: (base) => ({
          ...base,
          rx: 12,
          stroke: "white",
          strokeWidth: 3,
        }),
        highlightedArea: (base) => ({
          ...base,
          stroke: "white",
          strokeWidth: 3,
        }),
        maskWrapper: (base) => ({
          ...base,
          color: "black",
        }),
        close: (base) => ({
          ...base,
          color: "red",
          transform: "scale(1.5)",
        }),
      }}
    >
      <DisableScrollWhenTourOpen />
      <ActiveMaskTracker />
      <ScrollToTopOnFirstStep />
      <ResetTourOnClose />

      <GameRefsContext.Provider
        value={{
          jackpotRef,
          coinFlipRef,
          betFormRef,
          historyRef,
          tabsRef,
          balanceRef,
          pointsRef,
          depositRef,
          betAmountRef,
          betChoiceRef,
          betSideRef,
          winnersRef,
        }}
      >
        <Outlet />
      </GameRefsContext.Provider>
    </TourProvider>
  );
};

export default GameLayout;
/********old-code-used-in-prod********************************************************************************************************************************************************************************************************************************* */

// import { createContext, useContext, useEffect, useRef } from "react";
// import { Outlet } from "react-router-dom";
// import { TourProvider, useTour } from "@reactour/tour";

// export const GameRefsContext = createContext();
// export const useGameRefs = () => useContext(GameRefsContext);

// const DisableScrollWhenTourOpen = () => {
//   const { isOpen } = useTour();

//   useEffect(() => {
//     document.body.style.overflow = isOpen ? "hidden" : "";
//     return () => (document.body.style.overflow = "");
//   }, [isOpen]);

//   return null;
// };

// const CustomNavigation = () => {
//   const { setIsOpen, currentStep, steps, setCurrentStep } = useTour();
//   const isLastStep = currentStep === steps.length - 1;

//   const handleNext = () => {
//     if (isLastStep) setIsOpen(false);
//     else setCurrentStep((prev) => prev + 1);
//   };
//   const handlePrev = () => {
//     if (currentStep > 0) setCurrentStep((prev) => prev - 1);
//   };

//   return (
//     <div className="flex justify-between mt-3">
//       <button
//         onClick={handlePrev}
//         className="border border-[#00ff88] text-[#00ff88] px-3 py-1 rounded hover:bg-[#00ff88] hover:text-black transition"
//       >
//         Prev
//       </button>

//       <div className="flex gap-2">
//         <button
//           onClick={() => setIsOpen(false)}
//           className="text-gray-400 hover:text-white"
//         >
//           Skip
//         </button>
//         <button
//           onClick={handleNext}
//           className="bg-[#00ff88] text-black px-3 py-1 rounded hover:bg-[#00cc6f] transition"
//         >
//           {isLastStep ? "Finish" : "Next"}
//         </button>
//       </div>
//     </div>
//   );
// };

// // 🧩 Auto-tracking component (follows target even when it moves)
// const ActiveMaskTracker = () => {
//   const { currentStep, steps, isOpen, setCurrentStep } = useTour();

//   useEffect(() => {
//     if (!isOpen || !steps[currentStep]) return;

//     const selector = steps[currentStep]?.selector;
//     const element =
//       typeof selector === "string"
//         ? document.querySelector(selector)
//         : typeof selector === "function"
//         ? selector()
//         : selector?.current;

//     if (!element) return;

//     // Track position + resize
//     const observer = new ResizeObserver(() => {
//       // This forces Reactour to re-render and update mask position
//       setCurrentStep((prev) => prev);
//     });

//     const mutationObserver = new MutationObserver(() => {
//       setCurrentStep((prev) => prev);
//     });

//     observer.observe(element);
//     mutationObserver.observe(document.body, { childList: true, subtree: true });

//     // Also track scroll changes
//     const handleScroll = () => setCurrentStep((prev) => prev);
//     window.addEventListener("scroll", handleScroll, true);

//     return () => {
//       observer.disconnect();
//       mutationObserver.disconnect();
//       window.removeEventListener("scroll", handleScroll, true);
//     };
//   }, [isOpen, currentStep, steps, setCurrentStep]);

//   return null;
// };

// const GameLayout = () => {
//   const jackpotRef = useRef(null);
//   const coinFlipRef = useRef(null);
//   const betFormRef = useRef(null);
//   const historyRef = useRef(null);
//   const balanceRef = useRef(null);
//   const depositRef = useRef(null);
//   const betsideRef = useRef(null);
//   const pointsRef = useRef(null);
//   const betAmountRef = useRef(null);
//   const betChoiceRef = useRef(null);
//   const tabsRef = useRef(null);
//   const winnersRef = useRef(null);

//   const steps = [
//     {
//       selector: "#welcome-section",
//       content:
//         (<p>
//             😊 Welcome to Heads or Tails guide sequence on how the game works !! Press NEXT to continue.
//         </p>),
//     },
//     {
//       selector: "#balance-section",
//       content:
//         "💰 Track your account balance here and when you win your balance will update.",
//     },
//     {
//       selector: "#deposit-section",
//       content: " Click the button to deposit funds to your account.",
//     },
//     {
//       selector: "#bet-side-section",
//       content: "Track which side other players have choosen here.",
//     },
//     {
//       selector: "#jackpot-section",
//       content: "💰 This is the Jackpot section showing total pool value.",
//     },

//     {
//       selector: "#points-section",
//       content:
//         " Earn points everytime you bet and get to see how many points you have here.Click the points button to redeem your points to cash",
//     },

//     {
//       selector: "#coin-flip-section",
//       content: "🪙 Watch the coin flip here to see which side wins.",
//     },
//     {
//       selector: "#bet-form-section",
//       content: "🎯 Choose your side and place your bet here!",
//     },
//     {
//       selector: "#bet-amount-section",
//       content:
//         "💵 Pick an amount from the preset values or enter your desired amount in the input section",
//     },
//     {
//       selector: "#bet-choice-section",
//       content:
//         " You can either choose HEADS or TAILS and then press Place Your bet to confirm your pick",
//     },
//     {
//       selector: "#winners-section",
//       content:
//         "↗️ Toggle between daily,weekly or monthly top winners to see the top winnings.",
//     },
//     {
//       selector: "#history-section",
//       content: "📜 Check past results to see how rounds went.",
//     },
//     {
//       selector: "#tabs-section",
//       content: "📂 View your Active Bets, Bet History, and Top Wins here.",
//     },
//   ];

//   return (
//     <TourProvider
//       steps={steps}
//       scrollSmooth
//       padding={12}
//       className="reactour-dark"
//       showBadge={false}
//       showDots
//       showCloseButton
//       components={{
//         Navigation: CustomNavigation,
//       }}
//       styles={{
//         popover: (base) => ({
//           ...base,
//           background: "black",
//           color: "white",
//           borderRadius: "18px",
//           border: "1px solid #00ff88",
//         }),
//         maskArea: (base) => ({ ...base, rx: 12 }),
//         maskWrapper: (base) => ({
//           ...base,
//           color: "black",
//         }),
//         close: (base) => ({
//           ...base,
//           color: "red",
//           transform: "scale(1.5)",
//         }),
//       }}
//     >
//       <DisableScrollWhenTourOpen />
//       <ActiveMaskTracker />
//       <GameRefsContext.Provider
//         value={{
//           jackpotRef,
//           coinFlipRef,
//           betFormRef,
//           historyRef,
//           tabsRef,
//           balanceRef,
//           pointsRef,
//           depositRef,
//           betAmountRef,
//           betChoiceRef,
//           betsideRef,
//           winnersRef,
//         }}
//       >

//         <Outlet />
//       </GameRefsContext.Provider>
//     </TourProvider>
//   );
// };

// export default GameLayout;

/***************************************************************************************************************************************************** */

// import { createContext, useContext, useEffect, useRef, useState } from "react";
// import { Outlet } from "react-router-dom";
// import { TourProvider, useTour } from "@reactour/tour";

// export const GameRefsContext = createContext();
// export const useGameRefs = () => useContext(GameRefsContext);

// const DisableScrollWhenTourOpen = () => {
//   const { isOpen } = useTour();

//   useEffect(() => {
//     if (isOpen) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "";
//     }
//     return () => (document.body.style.overflow = "");
//   }, [isOpen]);

//   return null; // component just for side effects
// };

// const CustomNavigation = () => {
//   const { setIsOpen, currentStep, steps, setCurrentStep } = useTour();

//   const isLastStep = currentStep === steps.length - 1;

//   const handleNext = () => {
//     if (isLastStep) setIsOpen(false);
//     else setCurrentStep((prev) => prev + 1);
//   };

//   const handlePrev = () => {
//     if (currentStep > 0) setCurrentStep((prev) => prev - 1);
//   };

//   return (
//     <div className="flex justify-between mt-3">
//       <button
//         onClick={handlePrev}
//         className="border border-[#00ff88] text-[#00ff88] px-3 py-1 rounded hover:bg-[#00ff88] hover:text-black transition"
//       >
//         Prev
//       </button>

//       <div className="flex gap-2">
//         <button
//           onClick={() => setIsOpen(false)}
//           className="text-gray-400 hover:text-white"
//         >
//           Skip
//         </button>
//         <button
//           onClick={handleNext}
//           className="bg-[#00ff88] text-black px-3 py-1 rounded hover:bg-[#00cc6f] transition"
//         >
//           {isLastStep ? "Finish" : "Next"}
//         </button>
//       </div>
//     </div>
//   );
// };

// const GameLayout = () => {
//   const jackpotRef = useRef(null);
//   const coinFlipRef = useRef(null);
//   const betFormRef = useRef(null);
//   const historyRef = useRef(null);
//   const tabsRef = useRef(null);

//   const steps = [
//     {
//       selector: "#jackpot-section",
//       content: "💰 This is the Jackpot section showing total pool value.",
//     },
//     {
//       selector: "#coin-flip-section",
//       content: "🪙 Watch the coin flip here to see which side wins.",
//     },
//     {
//       selector: "#bet-form-section",
//       content: "🎯 Choose your side and place your bet here!You can either choose HEADS or TAILS",
//     },
//     {
//       selector: "#history-section",
//       content: "📜 Check past results to see how rounds went.",
//     },
//     {
//       selector: "#tabs-section",
//       content: "📂 View your Active Bets, Bet History, and Top Wins here.",
//     },
//   ];

//   return (
//     <TourProvider
//       steps={steps}
//       scrollSmooth
//       padding={12}
//       className="reactour-dark"
//       showBadge={false}
//       showDots={true}
//       showCloseButton={true}
//       components={{
//         Navigation: CustomNavigation,
//       }}
//       styles={{
//         popover: (base) => ({
//           ...base,
//           background: "black",
//           color: "white",
//           borderRadius: "18px",
//           border: "1px solid #00ff88",
//         }),
//         maskArea: (base) => ({ ...base, rx: 12 }),
//         maskWrapper: (base) => ({
//           ...base,
//           color: "black",
//           border: "1px solid  ",
//         }),
//         close: (base) => ({
//           ...base,
//           color: "red",
//           transform: "scale(1.5)",
//         }),
//       }}
//     >
//       <DisableScrollWhenTourOpen />
//       <GameRefsContext.Provider
//         value={{ jackpotRef, coinFlipRef, betFormRef, historyRef, tabsRef }}
//       >
//         <Outlet />
//       </GameRefsContext.Provider>
//     </TourProvider>
//   );
// };

// export default GameLayout;
